"use client";

import { useCallback, useMemo } from "react";
import {
  dimensionOptions,
  supportedLanguages,
  mindstringPrototypesSlang,
  getVisibleMindstringPrototypeCodes,
  backlogFocusOptions,
  aimOfAnalysisOptions,
  audienceBackgroundOptions,
  degreeOfImplicitenessOptions,
  degreeOfAbstractionOptions,
  degreeOfIntentionOptions,
  sentenceStructureOptions,
  dateConventionOptions,
  dateDetailOptions,
  timelineFramingOptions,
  sectionNamingStyleOptions,
  resumeReturnFormatOptions,
  visuallyEmphatizeOptions,
} from "@/shared/config/backend";
import {
  QuestionnaireViewSelectOptions,
  PagePathOptions,
  StudioGroupingOptions,
  StudioContentOptions,
} from "@/shared/config/app";
import { useAuth } from "@/state/globalStore";
import type { GenericIconType } from "../actions/GenericIcon";
import { QuestionnaireViewMode } from "@/shared/types";
import type { GenericOptionItem } from "./option.types";

// ---------------------------------------------
// Utils: Factory funtion to generate option items
// ---------------------------------------------

// Generic mapper: converts an array of any type T into itemObjects fit for selectors, switches, etc.
// Accepts either a key of T (to read a string/number value) or a getter function.
export function optionItemsFactoryFunction<T, V extends string = string>(
  items: T[] | null | undefined,
  labelKeyOrGetter: keyof T | ((item: T) => string),
  valueKeyOrGetter: keyof T | ((item: T) => V),
  descriptionKeyOrGetter?:
    | keyof T
    | ((item: T) => string)
    | ((item: T) => string | undefined),
): GenericOptionItem<V>[] {
  const resolveRequired = (
    item: T,
    keyOrGetter: keyof T | ((item: T) => string),
  ): string =>
    typeof keyOrGetter === "function"
      ? keyOrGetter(item)
      : String(item[keyOrGetter as keyof T]);

  const resolveOptional = (
    item: T,
    keyOrGetter:
      | keyof T
      | ((item: T) => string)
      | ((item: T) => string | undefined),
  ): string | undefined =>
    typeof keyOrGetter === "function"
      ? keyOrGetter(item)
      : String(item[keyOrGetter as keyof T]);

  return (items ?? []).map((item) => {
    const value = resolveRequired(item, valueKeyOrGetter) as V;
    return {
      label: resolveRequired(item, labelKeyOrGetter),
      value,
      description: descriptionKeyOrGetter
        ? resolveOptional(item, descriptionKeyOrGetter)
        : undefined,
    };
  });
}

export function useOptionItems() {
  const { isUserAdmin } = useAuth();

  // Selectors
  const languageItems = useMemo(
    () =>
      (
        Object.entries(supportedLanguages) as [
          keyof typeof supportedLanguages,
          string,
        ][]
      ).map(([code, name]) => ({
        label: name,
        value: code,
      })) as GenericOptionItem<keyof typeof supportedLanguages>[],
    [],
  );

  const questionnaireViewModeItems = useMemo(
    () =>
      Object.entries(QuestionnaireViewSelectOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<QuestionnaireViewMode>[],
    [],
  );

  // Cuts the prototype options for non-admin users
  const mindstringPrototypesSlangItems = useMemo(() => {
    const allowedKeys = getVisibleMindstringPrototypeCodes(isUserAdmin);

    return Object.entries(mindstringPrototypesSlang)
      .filter(([key]) =>
        allowedKeys.includes(key as keyof typeof mindstringPrototypesSlang),
      )
      .map(([value, { label, description, icon }]) => ({
        label,
        value,
        description,
        icon,
      })) as (GenericOptionItem<keyof typeof mindstringPrototypesSlang> & {
      description: string;
      icon: GenericIconType;
    })[];
  }, [isUserAdmin]);

  // Cuts "admin" for non-admin users //! Is function as Home should be optionally excluded in some cases. See if this is needed...
  const pagePathItems = useCallback(
    (excludeHome: boolean = false) => {
      const allowedKeys: (keyof typeof PagePathOptions)[] = isUserAdmin
        ? (Object.keys(PagePathOptions) as (keyof typeof PagePathOptions)[])
        : ["studio", "backlog"];

      return Object.entries(PagePathOptions)
        .filter(
          ([key]) =>
            allowedKeys.includes(key as keyof typeof PagePathOptions) &&
            !(excludeHome && key === "home"),
        )
        .map(([value, { label, description }]) => ({
          label,
          value,
          description,
        })) as (GenericOptionItem<keyof typeof PagePathOptions> & {
        description: string;
      })[];
    },
    [isUserAdmin],
  );

  const dimensionItems = useMemo(
    () =>
      Object.entries(dimensionOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof dimensionOptions>[],
    [],
  );

  const backlogFocusItems = useMemo(
    () =>
      Object.entries(backlogFocusOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof backlogFocusOptions>[],
    [],
  );

  const studioGroupingItems = useMemo(
    () =>
      Object.entries(StudioGroupingOptions).map(
        ([value, { label, description }]) => ({
          label,
          value,
          description,
        }),
      ) as (GenericOptionItem<keyof typeof StudioGroupingOptions> & {
        description: string;
      })[],
    [],
  );

  const studioContentItems = useMemo(
    () =>
      Object.entries(StudioContentOptions).map(
        ([value, { label, description }]) => ({
          label,
          value,
          description,
        }),
      ) as (GenericOptionItem<keyof typeof StudioContentOptions> & {
        description: string;
      })[],
    [],
  );

  const aimOfAnalysisItems = useMemo(
    () =>
      Object.entries(aimOfAnalysisOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof aimOfAnalysisOptions>[],
    [],
  );

  const audienceBackgroundItems = useMemo(
    () =>
      Object.entries(audienceBackgroundOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof audienceBackgroundOptions>[],
    [],
  );

  const degreeOfImplicitenessItems = useMemo(
    () =>
      Object.entries(degreeOfImplicitenessOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof degreeOfImplicitenessOptions>[],
    [],
  );

  const degreeOfAbstractionItems = useMemo(
    () =>
      Object.entries(degreeOfAbstractionOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof degreeOfAbstractionOptions>[],
    [],
  );

  const degreeOfIntentionItems = useMemo(
    () =>
      Object.entries(degreeOfIntentionOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof degreeOfIntentionOptions>[],
    [],
  );

  const sentenceStructureItems = useMemo(
    () =>
      Object.entries(sentenceStructureOptions).map(
        ([value, { label, description }]) => ({
          label,
          value,
          description,
        }),
      ) as GenericOptionItem<keyof typeof sentenceStructureOptions>[],
    [],
  );

  const dateConventionItems = useMemo(
    () =>
      Object.entries(dateConventionOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof dateConventionOptions>[],
    [],
  );

  const dateDetailItems = useMemo(
    () =>
      Object.entries(dateDetailOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof dateDetailOptions>[],
    [],
  );

  const timelineFramingItems = useMemo(
    () =>
      Object.entries(timelineFramingOptions).map(
        ([value, { label, description }]) => ({
          label,
          value,
          description,
        }),
      ) as GenericOptionItem<keyof typeof timelineFramingOptions>[],
    [],
  );

  const sectionNamingStyleItems = useMemo(
    () =>
      Object.entries(sectionNamingStyleOptions).map(
        ([value, { label, description }]) => ({
          label,
          value,
          description,
        }),
      ) as GenericOptionItem<keyof typeof sectionNamingStyleOptions>[],
    [],
  );

  const resumeReturnFormatItems = useMemo(
    () =>
      Object.entries(resumeReturnFormatOptions).map(([value, label]) => ({
        label,
        value,
      })) as GenericOptionItem<keyof typeof resumeReturnFormatOptions>[],
    [],
  );

  const visuallyEmphatizeItems = useMemo(
    () =>
      Object.entries(visuallyEmphatizeOptions).map(
        ([value, { label, description }]) => ({
          label,
          value,
          description,
        }),
      ) as GenericOptionItem<keyof typeof visuallyEmphatizeOptions>[],
    [],
  );

  return {
    languageItems,
    questionnaireViewModeItems,
    dimensionItems,
    backlogFocusItems,

    studioGroupingItems,
    studioContentItems,
    mindstringPrototypesSlangItems,
    pagePathItems, //! it is a function
    aimOfAnalysisItems,
    audienceBackgroundItems,
    degreeOfImplicitenessItems,
    degreeOfAbstractionItems,
    degreeOfIntentionItems,
    sentenceStructureItems,
    dateConventionItems,
    dateDetailItems,
    timelineFramingItems,
    sectionNamingStyleItems,
    resumeReturnFormatItems,
    visuallyEmphatizeItems,
  };
}
